import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const COPY = {
  en: {
    title: "SHOWREEL",
    tag: "ANIMATION • PROCESS",
    sound_on: "TURN ON SOUND 🔊",
    sound_off: "MUTE 🔇",
    description: "Key commercial and author projects of the studio recently.",
    watch: "Watch",
    no_video: "No video yet",
  },
  ru: {
    title: "ШОУРИЛ",
    tag: "АНИМАЦИЯ • ПРОЦЕСС",
    sound_on: "ВКЛЮЧИТЬ ЗВУК 🔊",
    sound_off: "ВЫКЛЮЧИТЬ ЗВУК 🔇",
    description: "Ключевые коммерческие и авторские проекты студии за последнее время.",
    watch: "Смотреть",
    no_video: "Видео пока нет",
  },
  es: {
    title: "SHOWREEL",
    tag: "ANIMACIÓN · PROCESO",
    sound_on: "ACTIVAR SONIDO 🔊",
    sound_off: "SILENCIAR 🔇",
    description: "Proyectos comerciales y de autor clave del estudio últimamente.",
    watch: "Ver",
    no_video: "Aún no hay video",
  },
};

// Showreel video (Vimeo). TODO: mover a site_config para que Ksenia lo cambie sola desde el panel.
const SHOWREEL_URL =
  "https://player.vimeo.com/video/1219262592?h=6aef1ba7ff&autoplay=1&loop=1&muted=1&controls=1&title=0&byline=0&portrait=0&dnt=1";

export default function FeaturedWork() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    api.get("/featured").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (window.Vimeo && iframeRef.current && !playerRef.current) {
      playerRef.current = new window.Vimeo.Player(iframeRef.current);
    }
  }, []);

  const toggleSound = () => {
    const p = playerRef.current;
    if (!p) return;
    if (soundOn) {
      p.setMuted(true);
      setSoundOn(false);
    } else {
      p.setMuted(false);
      p.play();
      setSoundOn(true);
    }
  };

  const pick = (obj, fallback = "") =>
    (obj && (obj[lang] || obj.en || obj.ru || obj.es)) || fallback;

  return (
    <section className="relative bg-white py-24 lg:py-32 border-t border-[#000000] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.5]">
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#ffd9ec] blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#cfeeff] blur-3xl" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
        {/* Showreel — text on the side, video beside it (Ksenia: letters next to the video) */}
        <div className="mt-10 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Video */}
          <div className="lg:col-span-7">
            <div className="relative aspect-video bg-black border border-[#000000] overflow-hidden">
              <iframe
                ref={iframeRef}
                src={SHOWREEL_URL}
                title={c.title}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                allowFullScreen
              />
            </div>
            {/* Magenta line under the video frame */}
            <div className="h-[3px] bg-[#8E009C]" />
          </div>

          {/* Text column */}
          <div className="lg:col-span-5">
            <h2 className="font-display uppercase tracking-tight leading-none text-[#3A0F46] text-[clamp(2.5rem,5vw,5.5rem)]">
              {c.title}
            </h2>
            {/* Tag — OCULTO por ahora (feedback: borrar "АНИМАЦИЯ • ПРОЦЕСС") */}
            {/* <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8f9a]">{c.tag}</div> */}
            <p className="mt-6 max-w-md font-sans text-sm text-[#4a4a4a] leading-relaxed">{c.description}</p>
            <div className="mt-8">
              <button
                onClick={toggleSound}
                data-testid="showreel-sound"
                className="inline-flex items-center gap-3 px-6 py-3 border border-[#000000] font-mono text-xs uppercase tracking-[0.2em] text-[#000000] hover:bg-[#000000] hover:text-white transition-colors"
              >
                {soundOn ? c.sound_off : c.sound_on}
              </button>
            </div>
          </div>
        </div>

        {/* ===== TARJETAS DE PROYECTOS ANTERIORES (videos viejos) — OCULTADAS por ahora, conservadas en comentario =====
        {items.length > 0 && (
          <div className="mt-24 lg:mt-32 space-y-12 lg:space-y-20">
            {items.map((p, i) => {
              const reverse = i % 2 === 1;
              const client = pick(p.client);
              const desc = pick(p.description);
              return (
                <motion.article
                  key={p.featured_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7 }}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}
                >
                  <div className="lg:col-span-7 [direction:ltr]">
                    <button
                      onClick={() => setPlaying(p)}
                      data-testid={`featured-play-${p.featured_id}`}
                      className="group relative block w-full aspect-[16/10] overflow-hidden border border-[#000000]"
                      style={{ boxShadow: `12px 12px 0 0 ${p.accent}` }}
                    >
                      {p.image ? (
                        <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
                      ) : (
                        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${p.accent}, #000000)` }} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/70 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full border-2 border-white flex items-center justify-center bg-white/10 backdrop-blur-sm group-hover:scale-110 group-hover:bg-white group-hover:text-[#000000] transition-all duration-500 text-white">
                          <Play size={28} fill="currentColor" className="ml-1" />
                        </div>
                      </div>
                      <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white">
                        № {String(i + 1).padStart(2, "0")}
                      </div>
                    </button>
                  </div>

                  <div className="lg:col-span-5 [direction:ltr]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: p.accent }}>
                      {client}
                    </div>
                    <h3 className="font-mono font-bold tracking-tight text-3xl lg:text-4xl text-[#000000]">
                      {p.title}
                    </h3>
                    <p className="mt-5 font-sans text-base lg:text-lg text-[#4a4a4a] leading-relaxed">
                      {desc}
                    </p>
                    <button
                      onClick={() => setPlaying(p)}
                      className="mt-7 inline-flex items-center gap-3 px-5 py-3 border border-[#000000] font-mono text-[11px] uppercase tracking-[0.2em] text-[#000000] hover:bg-[#000000] hover:text-white transition-colors"
                    >
                      <Play size={12} fill="currentColor" />
                      {c.watch}
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
        ===== FIN TARJETAS ANTERIORES ===== */}
      </div>

      <AnimatePresence>
        {playing && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setPlaying(null)}
            className="fixed inset-0 z-50 bg-[#000000]/95 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="featured-modal"
          >
            <button
              onClick={() => setPlaying(null)} data-testid="featured-modal-close"
              className="absolute top-6 right-6 p-2 border border-white text-white hover:bg-white hover:text-[#000000] transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()}
              className="w-full max-w-5xl"
            >
              <div className="aspect-video bg-black border border-white">
                {playing.youtube_id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${playing.youtube_id}?autoplay=1&rel=0&loop=1&playlist=${playing.youtube_id}`}
                    title={playing.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 font-mono text-sm">{c.no_video}</div>
                )}
              </div>
              <div className="mt-4 text-white">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">{pick(playing.client)}</div>
                <h3 className="mt-1 font-mono font-bold tracking-tight text-xl">{playing.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
